import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, Input, Select, Button, Text, Flex, Progress, useToast, Checkbox, CheckboxGroup,
    SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, Stack, FormControl, FormLabel, FormErrorMessage
} from '@chakra-ui/react';
import { FaFileCsv, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { startMovimentacaoEstoqueExport, getExportStatus, downloadExportFile } from "../services/api";
import { useUsuarios, semBloqueados } from "../hooks/useUsuarios";

const ACOES = [
    { value: 'entrada', label: 'Entrada' },
    { value: 'saida', label: 'Saída' },
    { value: 'balanco', label: 'Balanço' },
    { value: 'furo', label: 'Furo de Estoque' },
];
const ALL_ACOES = ACOES.map(a => a.value);

const USUARIO_SISTEMA = '0';
const MAX_DIAS_PERIODO = 366;

const dataLocal = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const periodoPadrao = () => {
    const hoje = new Date();
    return { data_inicio: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), data_fim: dataLocal(hoje) };
};

const validarPeriodo = ({ data_inicio, data_fim }) => {
    if (!data_inicio || !data_fim) return 'Informe a data inicial e a final.';
    if (data_fim < data_inicio) return 'A data final deve ser igual ou posterior à inicial.';
    const dias = (new Date(`${data_fim}T00:00:00`) - new Date(`${data_inicio}T00:00:00`)) / 86400000;
    if (dias > MAX_DIAS_PERIODO) return `O período máximo é de ${MAX_DIAS_PERIODO} dias.`;
    return null;
};

const validarProduto = (produtoId) => {
    if (produtoId === '') return null;
    return /^[1-9]\d*$/.test(produtoId) ? null : 'Informe só o número do ID do produto.';
};

const primeiroErro = (error) => {
    const errors = error?.response?.data?.errors;
    if (errors) {
        const primeiro = Object.values(errors).flat()[0];
        if (primeiro) return primeiro;
    }
    if (error?.response?.status === 403) return 'Exportação restrita a administradores.';
    return error?.response?.data?.message || 'Ocorreu um erro inesperado.';
};

const ExportarMovimentacaoEstoque = () => {
    const [filters, setFilters] = useState(() => ({ ...periodoPadrao(), acoes: ALL_ACOES, usuario_id: '', produto_id: '' }));
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const usuarios = useUsuarios();
    const toast = useToast();
    const intervalRef = useRef(null);

    const erroPeriodo = validarPeriodo(filters);
    const erroProduto = validarProduto(filters.produto_id);

    const pararPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: name === 'produto_id' ? value.trim() : value }));
    };

    const handleAcoesChange = (acoes) => setFilters(prev => ({ ...prev, acoes }));

    const handleStartExport = async () => {
        const erro = erroPeriodo || erroProduto || (filters.acoes.length === 0 ? 'Selecione ao menos uma ação.' : null);
        if (erro) {
            toast({ title: 'Filtro inválido', description: erro, status: 'warning', duration: 5000, isClosable: true });
            return;
        }

        pararPolling();
        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startMovimentacaoEstoqueExport({
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim,
                acoes: filters.acoes.length === ALL_ACOES.length ? [] : filters.acoes,
                usuario_id: filters.usuario_id === '' ? null : Number(filters.usuario_id),
                produto_id: filters.produto_id === '' ? null : Number(filters.produto_id),
            });
            setJobId(data.job_id);
            setJobStatus({ status: 'pending', progress: 0, message: 'Na fila de exportação...' });
        } catch (error) {
            toast({ title: 'Erro ao iniciar a exportação', description: primeiroErro(error), status: 'error', duration: 9000, isClosable: true });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                pararPolling();
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setJobStatus({ status: 'failed', message: 'Não foi possível acompanhar a exportação. Tente gerar de novo.' });
            setIsGenerating(false);
            pararPolling();
        }
    }, [jobId, pararPolling]);

    useEffect(() => {
        if (isGenerating && jobId && !intervalRef.current) {
            intervalRef.current = setInterval(fetchStatus, 3000);
        }
        return pararPolling;
    }, [isGenerating, jobId, fetchStatus, pararPolling]);

    const handleDownload = async () => {
        if (!jobStatus?.file_name) return;
        setIsDownloading(true);
        try {
            await downloadExportFile(jobStatus.file_name);
            toast({ title: 'Download iniciado', status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: 'Erro no download', description: 'Não foi possível baixar o arquivo. Gere a exportação de novo.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setIsDownloading(false);
            setJobStatus(null);
            setJobId(null);
        }
    };

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1} gap={2}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="orange.500">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress || 0} size="md" colorScheme="orange" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent" alignItems="flex-start">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo pronto!</AlertTitle>
                            <AlertDescription display="block" fontSize="sm" mb={3}>{jobStatus.message}</AlertDescription>
                            <Button leftIcon={<FaDownload />} colorScheme="green" onClick={handleDownload} isLoading={isDownloading} loadingText="Baixando..." size="sm" w={{ base: 'full', sm: 'auto' }}>
                                Baixar Planilha
                            </Button>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na exportação</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="5xl" mx="auto">
                <Box textAlign={{ base: 'center', md: 'left' }} px={{ base: 10, md: 0 }}>
                    <Heading color="#F68633" size="lg">Exportar Movimentação de Estoque</Heading>
                </Box>

                <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>1. Filtros</Heading>
                    <Stack spacing={5}>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                            <FormControl isInvalid={!!erroPeriodo}>
                                <FormLabel fontSize="sm" mb={1}>Data inicial</FormLabel>
                                <Input type="date" name="data_inicio" value={filters.data_inicio} onChange={handleFilterChange} max={filters.data_fim || undefined} />
                            </FormControl>
                            <FormControl isInvalid={!!erroPeriodo}>
                                <FormLabel fontSize="sm" mb={1}>Data final</FormLabel>
                                <Input type="date" name="data_fim" value={filters.data_fim} onChange={handleFilterChange} min={filters.data_inicio || undefined} />
                                {erroPeriodo && <FormErrorMessage>{erroPeriodo}</FormErrorMessage>}
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" mb={1}>Usuário</FormLabel>
                                <Select name="usuario_id" value={filters.usuario_id} onChange={handleFilterChange}>
                                    <option value="">Todos os usuários</option>
                                    <option value={USUARIO_SISTEMA}>Sistema</option>
                                    {semBloqueados(usuarios).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isInvalid={!!erroProduto}>
                                <FormLabel fontSize="sm" mb={1}>ID do produto</FormLabel>
                                <Input name="produto_id" value={filters.produto_id} onChange={handleFilterChange} placeholder="Todos" inputMode="numeric" />
                                {erroProduto && <FormErrorMessage>{erroProduto}</FormErrorMessage>}
                            </FormControl>
                        </SimpleGrid>

                        <Box>
                            <Heading size="sm" mb={3}>Ação</Heading>
                            <Checkbox
                                colorScheme="orange"
                                isChecked={filters.acoes.length === ALL_ACOES.length}
                                isIndeterminate={filters.acoes.length > 0 && filters.acoes.length < ALL_ACOES.length}
                                onChange={(e) => handleAcoesChange(e.target.checked ? ALL_ACOES : [])}
                                mb={3}
                                fontWeight="bold"
                            >
                                Todas as ações
                            </Checkbox>
                            <CheckboxGroup colorScheme="orange" value={filters.acoes} onChange={handleAcoesChange}>
                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                    {ACOES.map(a => <Checkbox key={a.value} value={a.value}>{a.label}</Checkbox>)}
                                </SimpleGrid>
                            </CheckboxGroup>
                        </Box>
                    </Stack>
                </Box>

                <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>2. Gere o arquivo</Heading>
                    <VStack spacing={4}>
                        <Button
                            leftIcon={<Icon as={FaFileCsv} />}
                            colorScheme="orange"
                            onClick={handleStartExport}
                            isLoading={isGenerating}
                            loadingText="Gerando..."
                            isDisabled={isGenerating || !!erroPeriodo || !!erroProduto || filters.acoes.length === 0}
                            w="full"
                            size="lg"
                        >
                            Gerar Planilha
                        </Button>
                        {renderStatus()}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExportarMovimentacaoEstoque;
